/**
 * EmployeesSection — Phase 4c++++++ (Feb 2026)
 *
 * Kollabierbare Sektion auf der Settings-Seite (Dashboard?tab=settings),
 * die alle Mitarbeiter des Händlers verwaltet. Wurde aus dem Header-
 * Dropdown entfernt und hier zwischen "Stammdaten" und "2FA" platziert.
 *
 * Verlinkt zur vollständigen Mitarbeiter-Seite `/dealer/employees` für
 * komplexere Aktionen (Funktion ändern, Aktivieren/Deaktivieren etc.).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Plus, ExternalLink, MessageCircle, Phone, UserCheck, Check, X, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import useSettingsExpandAll from "../lib/useSettingsExpandAll";

const FN_LABEL = {
    verkaeufer: "Verkauf",
    verkaufsleiter: "Verkaufsleitung",
    kundendienst: "Kundendienst",
    mechaniker: "Werkstatt",
    filialleiter: "Filialleitung",
    gebietsleitung: "Gebietsleitung",
    disposition: "Disposition",
    buchhaltung: "Buchhaltung",
    geschaeftsleitung: "Geschäftsleitung",
};

// Firmen-Module, die ein Händler einem bestätigten Mitarbeiter freigeben kann (Phase 3).
const MODULES = [
    { key: "listings", label: "Inserate" },
    { key: "inquiries", label: "Anfragen" },
    { key: "wms", label: "Lager / WMS" },
    { key: "workshop", label: "Werkstatt" },
    { key: "accounting", label: "Buchhaltung" },
    { key: "dispo", label: "Disposition" },
];

// Rechte-Vorlagen (P1) — schnelles Setzen typischer Modul-Kombinationen.
const PERMISSION_TEMPLATES = [
    { key: "verkaeufer", label: "Verkäufer", modules: ["listings", "inquiries", "dispo"] },
    { key: "werkstatt", label: "Werkstatt", modules: ["workshop", "inquiries"] },
    { key: "filialleiter", label: "Filialleiter", modules: ["listings", "inquiries", "wms", "workshop", "accounting", "dispo"] },
];

export default function EmployeesSection({ currentUser }) {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false); // Default: eingeklappt
    useSettingsExpandAll(setExpanded);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [requests, setRequests] = useState([]); // eingehende Mitarbeiter-Anfragen
    const [accessRequests, setAccessRequests] = useState([]); // Modul-Freigabe-Anfragen

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await api.get("/dealer/employees");
            setItems(r.data?.items || []);
        } catch (e) {
            setError(e.response?.data?.detail || "Mitarbeiter konnten nicht geladen werden");
        }
        try {
            const rq = await api.get("/dealer/employment-requests");
            setRequests(rq.data?.items || []);
        } catch { /* noop */ }
        try {
            const ar = await api.get("/dealer/access-requests");
            setAccessRequests(ar.data?.items || []);
        } catch { /* noop */ }
        setLoading(false);
    };

    const decideAccess = async (req, action) => {
        try {
            await api.post(`/dealer/access-requests/${req.id}/${action}`);
            toast.success(action === "grant" ? "Modul freigegeben" : "Anfrage abgelehnt");
            setAccessRequests((rs) => rs.filter((x) => x.id !== req.id));
            if (action === "grant") {
                setRequests((rs) => rs.map((x) => (x.employee_id === req.employee_id && x.status === "confirmed"
                    ? { ...x, permissions: [...new Set([...(x.permissions || []), req.module])] }
                    : x)));
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        }
    };

    const decide = async (req, decision) => {
        try {
            const r = await api.post(`/dealer/employment-requests/${req.id}/decide`, { decision });
            toast.success(decision === "confirmed" ? "Mitarbeiter bestätigt — Bereiche unten freigeben" : "Anfrage abgelehnt");
            setRequests((rs) => rs.map((x) => (x.id === req.id
                ? { ...x, status: decision, permissions: r.data?.permissions || x.permissions || [] }
                : x)));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        }
    };

    const savePermissions = async (req, perms) => {
        setRequests((rs) => rs.map((x) => (x.id === req.id ? { ...x, permissions: perms } : x)));
        try {
            await api.patch(`/dealer/employment-requests/${req.id}/permissions`, { permissions: perms });
            toast.success("Rechte gespeichert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
            load();
        }
    };

    const loadCounts = async () => {
        try {
            const rq = await api.get("/dealer/employment-requests");
            setRequests(rq.data?.items || []);
        } catch { /* noop */ }
        try {
            const ar = await api.get("/dealer/access-requests");
            setAccessRequests(ar.data?.items || []);
        } catch { /* noop */ }
    };

    useEffect(() => {
        loadCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (expanded && items.length === 0 && !loading) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    if (!currentUser || currentUser.role !== "b2b") return null;

    const active = items.filter((e) => e.is_active !== false);
    const pendingRequests = requests.filter((r) => r.status === "pending");
    const confirmedRequests = requests.filter((r) => r.status === "confirmed");
    const openRequestCount = pendingRequests.length + accessRequests.length;

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="employees-section">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className={`w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors ${expanded ? "border-b border-gray-200" : ""}`}
                data-testid="employees-section-toggle"
                aria-expanded={expanded}
            >
                <Users className="w-5 h-5 text-[#FFCC00] shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h2 className="font-display text-lg font-bold">Mitarbeiter</h2>
                        {openRequestCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[11px] font-bold font-mono-num rounded-full" data-testid="employees-pending-badge">
                                {openRequestCount}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                        Verkäufer, Kundendienst, Mechaniker & Filialleiter verwalten.
                        {active.length > 0 && <> · <strong className="font-mono-num">{active.length}</strong> aktiv</>}
                        {pendingRequests.length > 0 && <> · <strong className="font-mono-num text-amber-700">{pendingRequests.length}</strong> Anfrage{pendingRequests.length === 1 ? "" : "n"}</>}
                        {accessRequests.length > 0 && <> · <strong className="font-mono-num text-amber-700">{accessRequests.length}</strong> Freigabe-Anfrage{accessRequests.length === 1 ? "" : "n"}</>}
                    </p>
                </div>
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>

            {expanded && (
                <div className="p-5 space-y-4">
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 px-3 py-2 text-sm text-red-800">{error}</div>
                    )}

                    {/* Juni-2026 — Eingehende Mitarbeiter-Anfragen (Verkäufer möchte bestätigt werden) */}
                    {pendingRequests.length > 0 && (
                        <div className="border-2 border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="employment-requests-block">
                            <div className="flex items-center gap-2 mb-2">
                                <UserCheck className="w-4 h-4 text-amber-700" />
                                <h3 className="font-bold text-sm text-amber-900">Mitarbeiter-Anfragen ({pendingRequests.length})</h3>
                            </div>
                            <p className="text-[11px] text-amber-800 mb-3">Diese Personen geben an, bei dir tätig zu sein. Bestätige nur echte Mitarbeiter — bestätigte Mitarbeiter erhalten EUR-Provision.</p>
                            <ul className="space-y-2">
                                {pendingRequests.map((r) => (
                                    <li key={r.id} className="bg-white border border-amber-200 rounded-sm px-3 py-2 flex items-center gap-2" data-testid={`employment-request-${r.id}`}>
                                        <Briefcase className="w-4 h-4 text-gray-400 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold truncate">{r.employee_label}</div>
                                            <div className="text-[10px] text-gray-500">{r.activity_label}{r.employee_email && <> · {r.employee_email}</>}</div>
                                        </div>
                                        <button onClick={() => decide(r, "confirmed")} className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-2.5 py-1.5 rounded-sm" data-testid={`employment-confirm-${r.id}`}>
                                            <Check className="w-3.5 h-3.5" /> Bestätigen
                                        </button>
                                        <button onClick={() => decide(r, "rejected")} className="inline-flex items-center gap-1 border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-bold px-2.5 py-1.5 rounded-sm" data-testid={`employment-reject-${r.id}`}>
                                            <X className="w-3.5 h-3.5" /> Ablehnen
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Juni-2026 (Phase 3) — Modul-Freigabe-Anfragen der Mitarbeiter */}
                    {accessRequests.length > 0 && (
                        <div className="border border-amber-200 bg-amber-50 rounded-sm p-3" data-testid="access-requests-block">
                            <h3 className="font-bold text-sm mb-2 text-amber-800">Freigabe-Anfragen ({accessRequests.length})</h3>
                            <ul className="space-y-2">
                                {accessRequests.map((r) => (
                                    <li key={r.id} className="flex items-center gap-2 flex-wrap bg-white border border-amber-200 rounded-sm p-2" data-testid={`access-request-${r.id}`}>
                                        <span className="text-sm font-bold">{r.employee_label}</span>
                                        <span className="text-xs text-gray-500">möchte Zugriff auf</span>
                                        <span className="text-[10px] uppercase tracking-wider font-bold bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-sm">
                                            {(MODULES.find((m) => m.key === r.module) || {}).label || r.module}
                                        </span>
                                        <div className="ml-auto flex items-center gap-2">
                                            <button onClick={() => decideAccess(r, "grant")} className="text-xs font-bold text-green-700 hover:text-green-900" data-testid={`access-grant-${r.id}`}>Freigeben</button>
                                            <button onClick={() => decideAccess(r, "reject")} className="text-xs font-bold text-red-600 hover:text-red-800" data-testid={`access-reject-${r.id}`}>Ablehnen</button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Juni-2026 (Phase 3) — Bestätigte externe Mitarbeiter: Firmen-Bereiche freigeben */}
                    {confirmedRequests.length > 0 && (
                        <div className="border border-gray-200 rounded-sm p-3" data-testid="employment-confirmed-block">
                            <div className="flex items-center gap-2 mb-1">
                                <UserCheck className="w-4 h-4 text-green-700" />
                                <h3 className="font-bold text-sm">Bestätigte externe Mitarbeiter ({confirmedRequests.length})</h3>
                            </div>
                            <p className="text-[11px] text-gray-500 mb-3">Lege fest, welche Firmen-Bereiche jeder Mitarbeiter im „Firma"-Bereich sehen darf. Änderungen werden sofort gespeichert.</p>
                            <ul className="space-y-3">
                                {confirmedRequests.map((r) => (
                                    <li key={r.id} className="border border-gray-200 rounded-sm p-3" data-testid={`employment-confirmed-${r.id}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Briefcase className="w-4 h-4 text-gray-400 shrink-0" />
                                            <div className="text-sm font-bold truncate">{r.employee_label}</div>
                                            <span className="text-[10px] text-gray-500">{r.activity_label}</span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid={`employment-templates-${r.id}`}>
                                            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mr-0.5">Vorlage:</span>
                                            {PERMISSION_TEMPLATES.map((t) => {
                                                const cur = r.permissions || [];
                                                const isActive = t.modules.length === cur.length && t.modules.every((m) => cur.includes(m));
                                                return (
                                                    <button
                                                        key={t.key}
                                                        type="button"
                                                        onClick={() => savePermissions(r, [...t.modules])}
                                                        className={`px-2 py-0.5 rounded-sm border text-[11px] font-bold transition-colors ${isActive ? "border-[#FFCC00] bg-[#FFCC00] text-[#111827]" : "border-gray-300 bg-white text-gray-600 hover:border-[#111827]"}`}
                                                        data-testid={`employment-template-${r.id}-${t.key}`}
                                                    >
                                                        {t.label}
                                                    </button>
                                                );
                                            })}
                                            <button
                                                type="button"
                                                onClick={() => savePermissions(r, [])}
                                                className="px-2 py-0.5 rounded-sm border border-gray-300 bg-white text-gray-500 text-[11px] font-bold hover:border-red-400 hover:text-red-600 transition-colors"
                                                data-testid={`employment-template-${r.id}-clear`}
                                            >
                                                Zurücksetzen
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-2" data-testid={`employment-perms-${r.id}`}>
                                            {MODULES.map((m) => {
                                                const on = (r.permissions || []).includes(m.key);
                                                return (
                                                    <label
                                                        key={m.key}
                                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border cursor-pointer text-xs font-bold ${on ? "border-[#FFCC00] bg-[#FFFBEB] text-[#111827]" : "border-gray-200 bg-white text-gray-600"}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={on}
                                                            onChange={(e) => {
                                                                const cur = r.permissions || [];
                                                                const next = e.target.checked ? [...cur, m.key] : cur.filter((k) => k !== m.key);
                                                                savePermissions(r, next);
                                                            }}
                                                            className="w-3.5 h-3.5 accent-[#FFCC00]"
                                                            data-testid={`employment-perm-${r.id}-${m.key}`}
                                                        />
                                                        {m.label}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {loading && (
                        <div className="text-sm text-gray-500 py-4">Lade Mitarbeiter…</div>
                    )}

                    {!loading && items.length === 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-sm py-6 text-center">
                            <Users className="w-8 h-8 mx-auto text-gray-300" />
                            <div className="mt-2 text-sm text-gray-500">
                                Noch keine Mitarbeiter angelegt.
                            </div>
                        </div>
                    )}

                    {!loading && items.length > 0 && (
                        <div className="border border-gray-200 rounded-sm divide-y divide-gray-100 max-h-80 overflow-y-auto">
                            {items.slice(0, 50).map((e) => (
                                <div key={e.id} className="px-3 py-2 flex items-center gap-2 text-sm hover:bg-gray-50" data-testid={`emp-section-${e.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold truncate">
                                            {e.name || `${e.first_name || ""} ${e.last_name || ""}`.trim()}
                                            {e.site_code && <span className="ml-1.5 text-[10px] uppercase tracking-wider bg-[#FFF3BF] text-[#111827] px-1.5 py-0.5 rounded-sm font-mono-num" data-testid={`emp-site-code-${e.id}`} title={e.site_name || ""}>{e.site_code}</span>}
                                            {!e.has_login && <span className="ml-1.5 text-[10px] uppercase tracking-wider bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-sm">Kein Login</span>}
                                            {e.is_active === false && <span className="ml-1.5 text-[10px] uppercase tracking-wider bg-red-100 text-red-800 px-1.5 py-0.5 rounded-sm">Inaktiv</span>}
                                        </div>
                                        <div className="text-[10px] text-gray-500 font-mono-num">
                                            {e.employee_function ? FN_LABEL[e.employee_function] || e.employee_function : "—"}
                                            {e.mobile_phone && <> · {e.mobile_phone}</>}
                                            {e.email && !e.email.includes("@local.twowheels.info") && <> · {e.email}</>}
                                        </div>
                                    </div>
                                    {e.mobile_phone && (
                                        <>
                                            <a
                                                href={`https://wa.me/${e.mobile_phone.replace(/^\+/, "")}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-1.5 hover:bg-green-50 rounded-sm text-green-700"
                                                title="WhatsApp"
                                            ><MessageCircle className="w-3.5 h-3.5" /></a>
                                            <a
                                                href={`sms:${e.mobile_phone}`}
                                                className="p-1.5 hover:bg-blue-50 rounded-sm text-blue-700"
                                                title="SMS"
                                            ><Phone className="w-3.5 h-3.5" /></a>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2">
                        <button
                            onClick={() => navigate("/dealer/employees?new=1")}
                            className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black px-4 py-2 rounded-sm uppercase tracking-wider text-xs inline-flex items-center gap-2"
                            data-testid="emp-section-new-btn"
                        >
                            <Plus className="w-3.5 h-3.5" /> Neuer Mitarbeiter
                        </button>
                        <button
                            onClick={() => navigate("/dealer/employees")}
                            className="bg-white border border-gray-300 hover:bg-gray-100 text-[#111827] font-bold px-4 py-2 rounded-sm uppercase tracking-wider text-xs inline-flex items-center gap-2"
                            data-testid="emp-section-manage-btn"
                        >
                            <ExternalLink className="w-3.5 h-3.5" /> Alle Mitarbeiter verwalten
                        </button>
                    </div>
                    <p className="text-[10px] text-gray-500">
                        Auf der Mitarbeiter-Seite kannst du Funktionen ändern, Mitarbeiter aktivieren/deaktivieren und Nachrichten-Templates versenden.
                    </p>
                </div>
            )}
        </div>
    );
}
